import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { themes, getThemesForMode } from '@/lib/themes';
import { useSubscription } from '@/hooks/useSubscription';
import { previewTheme, applyTheme, getSavedThemeId } from '@/lib/themeManager';
import {
  Palette, Mic, Eye, Bell, Crown, BookOpen, LogOut, Download,
  Sun, Moon, Monitor, Lock, Check, BellRing, Pencil, User, Tag, X, FileText, Camera, Loader2, Plus,
  Save, Undo2,
} from 'lucide-react';
import { exportAsJSON, exportAsCSV, exportAsMarkdown } from '@/lib/export';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import type { TagsResponse } from '@/lib/api';
import { Input } from '@/components/ui/input';
import {
  getNotificationSettings,
  saveNotificationSettings,
  requestNotificationPermission,
  getNotificationPermission,
  type NotificationSettings,
} from '@/lib/notifications';
import PremiumModal from '@/components/PremiumModal';

type AiIntensity = 'raw' | 'light' | 'standard' | 'thorough';
type CardDisplayMode = 'compact' | 'standard' | 'rich';
type WeekStart = '0' | '1';

const FONT_SIZE_STEPS = [
  { value: 0, label: 'XS', px: '15px' },
  { value: 1, label: 'S', px: '16px' },
  { value: 2, label: 'M', px: '18px' },
  { value: 3, label: 'L', px: '20px' },
  { value: 4, label: 'XL', px: '22px' },
] as const;

interface SettingsSnapshot {
  themeMode: 'light' | 'dark' | 'system';
  colorTheme: string;
  aiIntensity: AiIntensity;
  weekStart: WeekStart;
  cardDisplay: CardDisplayMode;
  fontSizeIndex: number;
  aiSearch: boolean;
  aiAutoTag: boolean;
  reminderBanner: boolean;
  notifSettings: NotificationSettings;
}

export default function SettingsPage() {
  const { user, logout, updateUserName, updateUserPicture } = useAuth();
  const navigate = useNavigate();
  const { isPremium, refresh: refreshSubscription } = useSubscription();
  const { toast } = useToast();

  // --- Export ---
  const [exporting, setExporting] = useState<'json' | 'csv' | 'md' | null>(null);

  // --- Account ---
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(user?.name || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleNameSave = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === user?.name) {
      setEditingName(false);
      setNameInput(user?.name || '');
      return;
    }
    try {
      await updateUserName(trimmed);
      toast({ title: 'ニックネームを更新しました' });
    } catch {
      toast({ title: 'ニックネームの更新に失敗しました', variant: 'destructive' });
      setNameInput(user?.name || '');
    }
    setEditingName(false);
  };

  // --- Appearance ---
  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'system'>(
    () => (localStorage.getItem('theme') as 'light' | 'dark' | 'system') || 'light'
  );
  const dark = document.documentElement.classList.contains('dark');
  const [colorTheme, setColorTheme] = useState(() => getSavedThemeId());
  // --- Recording ---
  const [aiIntensity, setAiIntensity] = useState<AiIntensity>(() => {
    const saved = localStorage.getItem('aiIntensity');
    const migration: Record<string, AiIntensity> = {
      light_plus: 'light', standard_plus: 'standard',
    };
    const migrated = migration[saved as string] ?? saved;
    const valid: AiIntensity[] = ['raw', 'light', 'standard', 'thorough'];
    const result = valid.includes(migrated as AiIntensity) ? (migrated as AiIntensity) : 'light';
    if (saved !== result) localStorage.setItem('aiIntensity', result);
    return result;
  });
  const [weekStart, setWeekStart] = useState<WeekStart>(
    () => (localStorage.getItem('weekStartsOn') as WeekStart) || '1'
  );

  // --- Display ---
  const [cardDisplay, setCardDisplay] = useState<CardDisplayMode>(
    () => (localStorage.getItem('cardDisplay') as CardDisplayMode) || 'standard'
  );
  const [fontSizeIndex, setFontSizeIndex] = useState<number>(
    () => {
      const saved = localStorage.getItem('fontSizeIndex');
      if (saved !== null) return Number(saved);
      // 旧形式からの移行
      const legacy = localStorage.getItem('fontSize');
      if (legacy === 'small') return 1;
      if (legacy === 'large') return 3;
      return 2; // medium default
    }
  );

  // --- AI Search ---
  const [aiSearch, setAiSearch] = useState(() => localStorage.getItem('aiSearch') !== 'false');

  // --- AI Auto Tag ---
  const [aiAutoTag, setAiAutoTag] = useState(() => localStorage.getItem('aiAutoTag') !== 'false');
  const [showAutoTagDialog, setShowAutoTagDialog] = useState(false);

  // --- Tags ---
  const [tagsData, setTagsData] = useState<TagsResponse | null>(null);
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [newCustomTag, setNewCustomTag] = useState('');
  const [tagSaving, setTagSaving] = useState(false);

  // --- Premium Modal ---
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // --- Notifications ---
  const [reminderBanner, setReminderBanner] = useState(
    () => localStorage.getItem('reminderBanner') !== 'false'
  );
  const [notifSettings, setNotifSettings] = useState<NotificationSettings>(getNotificationSettings);
  const [notifPermission, setNotifPermission] = useState(getNotificationPermission);

  // サーバーから通知設定を読み込み（初回マウント時）
  useEffect(() => {
    import('@/lib/pushNotifications').then(({ loadNotificationSettingsFromServer }) => {
      loadNotificationSettingsFromServer().then((serverSettings) => {
        if (serverSettings) {
          setNotifSettings(serverSettings);
          // ローカルも同期
          localStorage.setItem('notificationSettings', JSON.stringify(serverSettings));
        }
      });
    });
  }, []);

  // ============================================================
  // Deferred Save: track original values and dirty state
  // ============================================================
  const originalRef = useRef<SettingsSnapshot>({
    themeMode, colorTheme, aiIntensity, weekStart,
    cardDisplay, fontSizeIndex, aiSearch, aiAutoTag,
    reminderBanner, notifSettings: { ...notifSettings },
  });
  const [snapshotVersion, setSnapshotVersion] = useState(0);

  const isDirty = useMemo(() => {
    const o = originalRef.current;
    return themeMode !== o.themeMode
      || colorTheme !== o.colorTheme
      || aiIntensity !== o.aiIntensity
      || weekStart !== o.weekStart
      || cardDisplay !== o.cardDisplay
      || fontSizeIndex !== o.fontSizeIndex
      || aiSearch !== o.aiSearch
      || aiAutoTag !== o.aiAutoTag
      || reminderBanner !== o.reminderBanner
      || JSON.stringify(notifSettings) !== JSON.stringify(o.notifSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeMode, colorTheme, aiIntensity, weekStart, cardDisplay, fontSizeIndex, aiSearch, aiAutoTag, reminderBanner, notifSettings, snapshotVersion]);

  const handleSaveAll = useCallback(async () => {
    // Theme
    localStorage.setItem('theme', themeMode);
    applyTheme(colorTheme);
    // Recording
    localStorage.setItem('aiIntensity', aiIntensity);
    localStorage.setItem('weekStartsOn', weekStart);
    // Display
    localStorage.setItem('cardDisplay', cardDisplay);
    localStorage.setItem('fontSizeIndex', String(fontSizeIndex));
    document.documentElement.style.fontSize = FONT_SIZE_STEPS[fontSizeIndex].px;
    localStorage.setItem('aiSearch', aiSearch ? 'true' : 'false');
    // Tags
    localStorage.setItem('aiAutoTag', aiAutoTag ? 'true' : 'false');
    // Notifications
    localStorage.setItem('reminderBanner', reminderBanner ? 'true' : 'false');
    await saveNotificationSettings(notifSettings);
    // Update snapshot and trigger isDirty recalculation
    originalRef.current = {
      themeMode, colorTheme, aiIntensity, weekStart,
      cardDisplay, fontSizeIndex, aiSearch, aiAutoTag,
      reminderBanner, notifSettings: { ...notifSettings },
    };
    setSnapshotVersion(v => v + 1);
    toast({ title: '設定を保存しました' });
  }, [themeMode, colorTheme, aiIntensity, weekStart, cardDisplay, fontSizeIndex, aiSearch, aiAutoTag, reminderBanner, notifSettings, toast]);

  const handleDiscardAll = useCallback(() => {
    const o = originalRef.current;
    setThemeMode(o.themeMode);
    setColorTheme(o.colorTheme);
    setAiIntensity(o.aiIntensity);
    setWeekStart(o.weekStart);
    setCardDisplay(o.cardDisplay);
    setFontSizeIndex(o.fontSizeIndex);
    setAiSearch(o.aiSearch);
    setAiAutoTag(o.aiAutoTag);
    setReminderBanner(o.reminderBanner);
    setNotifSettings({ ...o.notifSettings });
    // Revert visual previews
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = o.themeMode === 'dark' || (o.themeMode === 'system' && prefersDark);
    document.documentElement.classList.toggle('dark', shouldBeDark);
    previewTheme(o.colorTheme);
    document.documentElement.style.fontSize = FONT_SIZE_STEPS[o.fontSizeIndex].px;
  }, []);

  // --- Confirmation dialogs ---
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const allowLeaveRef = useRef(false);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const pendingUrlRef = useRef<string | null>(null);

  // Browser refresh/close guard
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Back button guard
  useEffect(() => {
    const handler = () => {
      if (allowLeaveRef.current || !isDirtyRef.current) return;
      window.history.pushState(null, '');
      setShowLeaveDialog(true);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // In-app navigation guard (intercept pushState from React Router)
  useEffect(() => {
    const originalPushState = window.history.pushState;
    window.history.pushState = function (...args: Parameters<typeof originalPushState>) {
      if (!isDirtyRef.current || allowLeaveRef.current) {
        return originalPushState.apply(window.history, args);
      }
      const url = args[2];
      if (url) {
        pendingUrlRef.current = url.toString();
        setShowLeaveDialog(true);
      }
    };
    return () => { window.history.pushState = originalPushState; };
  }, []);

  const leaveWithoutSaving = () => {
    setShowLeaveDialog(false);
    handleDiscardAll();
    allowLeaveRef.current = true;
    const url = pendingUrlRef.current;
    pendingUrlRef.current = null;
    if (url) { navigate(url); } else { navigate(-1); }
  };

  const leaveWithSaving = () => {
    setShowLeaveDialog(false);
    handleSaveAll();
    allowLeaveRef.current = true;
    const url = pendingUrlRef.current;
    pendingUrlRef.current = null;
    if (url) { navigate(url); } else { navigate(-1); }
  };

  const handleLeaveCancel = () => {
    setShowLeaveDialog(false);
    pendingUrlRef.current = null;
  };

  // Load tags on mount
  useEffect(() => {
    api.getTags().then(setTagsData).catch((err) => {
      console.warn('[Settings] タグの読み込みに失敗:', err);
    });
  }, []);

  // --- Handlers (preview only, no persistence) ---

  const handleThemeMode = (mode: 'light' | 'dark' | 'system') => {
    setThemeMode(mode);
    // Visual preview only
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = mode === 'dark' || (mode === 'system' && prefersDark);
    document.documentElement.classList.toggle('dark', shouldBeDark);
    // Re-apply color theme for the new mode
    previewTheme(colorTheme);
  };

  const handleColorTheme = (themeId: string) => {
    const theme = themes.find(t => t.id === themeId);
    if (theme?.premium && !isPremium) {
      setShowPremiumModal(true);
      return;
    }
    setColorTheme(themeId);
    previewTheme(themeId);
  };

  const handleAiIntensity = (value: AiIntensity) => {
    setAiIntensity(value);
  };

  const handleWeekStart = (value: WeekStart) => {
    setWeekStart(value);
  };

  const handleTagRename = async (oldTag: string) => {
    const trimmed = tagInput.trim();
    if (!trimmed || trimmed === oldTag) {
      setEditingTag(null);
      return;
    }
    setTagSaving(true);
    try {
      const result = await api.renameTag(oldTag, trimmed);
      if (result.merged) {
        toast({ title: `「${trimmed}」にマージしました（${result.affectedEntries}件のエントリを更新）` });
      } else {
        toast({ title: `タグ名を変更しました（${result.affectedEntries}件のエントリを更新）` });
      }
      // タグ一覧を再取得
      const updated = await api.getTags();
      setTagsData(updated);
    } catch {
      toast({ title: 'タグ名の変更に失敗しました', variant: 'destructive' });
    }
    setTagSaving(false);
    setEditingTag(null);
  };

  const handleTagDelete = async (tag: string) => {
    setTagSaving(true);
    try {
      await api.deleteTag(tag);
      const updated = await api.getTags();
      setTagsData(updated);
      toast({ title: `「${tag}」を削除しました` });
    } catch {
      toast({ title: 'タグの削除に失敗しました', variant: 'destructive' });
    }
    setTagSaving(false);
  };

  const handleAddCustomTag = async () => {
    const normalized = newCustomTag.trim().toLowerCase().replace(/\s+/g, '-');
    if (!normalized) return;
    setTagSaving(true);
    try {
      await api.createCustomTag(normalized);
      const updated = await api.getTags();
      setTagsData(updated);
      setNewCustomTag('');
      toast({ title: `「${normalized}」を追加しました` });
    } catch {
      toast({ title: 'タグの追加に失敗しました', variant: 'destructive' });
    }
    setTagSaving(false);
  };

  const handleCardDisplay = (value: CardDisplayMode) => {
    setCardDisplay(value);
  };

  const handleFontSize = (index: number) => {
    const step = FONT_SIZE_STEPS[index];
    if (!step) return;
    setFontSizeIndex(index);
    // Visual preview only
    document.documentElement.style.fontSize = step.px;
  };

  const handleReminderBanner = (checked: boolean) => {
    setReminderBanner(checked);
  };

  const handleNotifToggle = async (checked: boolean) => {
    if (checked) {
      const granted = await requestNotificationPermission();
      setNotifPermission(getNotificationPermission());
      if (!granted) {
        toast({ title: 'ブラウザの通知が許可されていません', description: 'ブラウザの設定で通知を許可してください', variant: 'destructive' });
        return;
      }
    }
    setNotifSettings(prev => ({ ...prev, enabled: checked }));
  };

  const handleNotifDay = (dayIndex: number, checked: boolean) => {
    setNotifSettings(prev => {
      const days = [...prev.days];
      days[dayIndex] = checked;
      return { ...prev, days };
    });
  };

  const handleNotifTime = (time: string) => {
    setNotifSettings(prev => ({ ...prev, time }));
  };

  const handleExport = async (format: 'json' | 'csv' | 'md') => {
    setExporting(format);
    try {
      if (format === 'json') await exportAsJSON();
      else if (format === 'csv') await exportAsCSV();
      else await exportAsMarkdown();
      toast({ title: 'エクスポートが完了しました' });
    } catch {
      toast({ title: 'エクスポートに失敗しました', variant: 'destructive' });
    }
    setExporting(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="space-y-6 pb-24">
      <h1 className="text-2xl font-bold">設定</h1>

      {/* ===================== 1. 外観 (Appearance) ===================== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="rounded-full p-1.5 bg-purple-500/10">
              <Palette className="h-5 w-5 text-purple-500" />
            </div>
            外観
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Theme mode: segment control */}
          <div>
            <Label className="text-sm font-medium mb-2 block">テーマモード</Label>
            <div className="flex gap-0.5 bg-muted rounded-lg p-0.5">
              {([
                { value: 'light' as const, label: 'ライト', icon: Sun },
                { value: 'dark' as const, label: 'ダーク', icon: Moon },
                { value: 'system' as const, label: 'システム', icon: Monitor },
              ]).map(opt => (
                <button
                  key={opt.value}
                  className={`flex items-center justify-center gap-1.5 flex-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    themeMode === opt.value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground'
                  }`}
                  onClick={() => handleThemeMode(opt.value)}
                >
                  <opt.icon className="h-3.5 w-3.5" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Theme selection - mode-filtered 2-color swatches */}
          <div>
            <Label className="text-sm font-medium mb-3 block">カラーテーマ</Label>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
              {getThemesForMode(dark ? 'dark' : 'light').map(theme => {
                const isSelected = colorTheme === theme.id;
                const isLocked = theme.premium && !isPremium;
                const colors = dark ? theme.dark : theme.light;
                const primaryHsl = colors['--theme-primary'];
                const bgHsl = colors['--theme-background'];
                return (
                  <button
                    key={theme.id}
                    onClick={() => handleColorTheme(theme.id)}
                    title={theme.name}
                  >
                    <div
                      className={`relative w-9 h-9 rounded-lg border-2 overflow-hidden transition-all flex flex-col ${
                        isSelected
                          ? 'border-primary ring-2 ring-primary/20 scale-110'
                          : 'border-border hover:scale-110'
                      }`}
                    >
                      <div className="flex-1" style={{ backgroundColor: `hsl(${bgHsl})` }} />
                      <div className="flex-1" style={{ backgroundColor: `hsl(${primaryHsl})` }} />
                      {isLocked && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Lock className="h-3 w-3 text-white drop-shadow" />
                        </div>
                      )}
                      {isSelected && !isLocked && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                          <Check className="h-3.5 w-3.5 text-white drop-shadow" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

        </CardContent>
      </Card>
      </motion.div>

      {/* ===================== 2. 記録 (Recording) ===================== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="rounded-full p-1.5 bg-orange-500/10">
              <Mic className="h-5 w-5 text-orange-500" />
            </div>
            記録
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* AI Intensity — segment control */}
          <div>
            <Label className="text-sm font-medium mb-2 block">仕上げレベル</Label>
            <div className="flex gap-0.5 bg-muted rounded-lg p-0.5">
              {([
                { value: 'raw' as const, label: '無加工' },
                { value: 'light' as const, label: 'フィラー除去' },
                { value: 'standard' as const, label: '整形' },
                { value: 'thorough' as const, label: 'リライト' },
              ]).map(opt => (
                <button
                  key={opt.value}
                  className={`flex-1 px-1.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    aiIntensity === opt.value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground'
                  }`}
                  onClick={() => handleAiIntensity(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {aiIntensity === 'raw' && '話した通りそのまま残したい人向け。フィラー（えっと、あの）もそのまま'}
              {aiIntensity === 'light' && '「えっと」「あの」等を除去。内容・語順・文体はそのまま'}
              {aiIntensity === 'standard' && '段落分け・文法修正・文体の揺れを統一。内容や意味は変えない'}
              {aiIntensity === 'thorough' && '段落を再構成し冗長な部分を圧縮。ブログ記事のように仕上げる'}
            </p>
          </div>

          {/* Week start day — segment control */}
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">週の開始日</Label>
            <div className="flex gap-0.5 bg-muted rounded-lg p-0.5">
              <button
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  weekStart === '1' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground'
                }`}
                onClick={() => handleWeekStart('1')}
              >
                月曜
              </button>
              <button
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  weekStart === '0' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground'
                }`}
                onClick={() => handleWeekStart('0')}
              >
                日曜
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
      </motion.div>

      {/* ===================== 2.5 カスタム辞書 (Custom Dictionary) ===================== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}>
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="rounded-full p-1.5 bg-blue-500/10">
              <BookOpen className="h-5 w-5 text-blue-500" />
            </div>
            カスタム辞書
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              固有名詞や専門用語を登録して、音声認識と文字起こしの精度を向上させます
            </p>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 ml-4 shrink-0"
              onClick={() => navigate('/dictionary')}
            >
              <BookOpen className="h-3.5 w-3.5" />
              辞書
            </Button>
          </div>
        </CardContent>
      </Card>
      </motion.div>

      {/* ===================== 3. タグ管理 (Tag Management) ===================== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="rounded-full p-1.5 bg-emerald-500/10">
              <Tag className="h-5 w-5 text-emerald-500" />
            </div>
            タグ管理
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* AI自動タグ付けトグル */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">AIによる自動タグ付け</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                日記保存時にAIがタグを自動付与します
              </p>
            </div>
            <Switch
              checked={aiAutoTag}
              onCheckedChange={(checked) => {
                if (!checked) {
                  setShowAutoTagDialog(true);
                } else {
                  setAiAutoTag(true);
                }
              }}
            />
          </div>

          {/* プリセットタグ */}
          <div>
            <Label className="text-sm font-medium mb-2 block">プリセットタグ</Label>
            <p className="text-xs text-muted-foreground mb-2">
              AIが自動付与するタグ候補（削除・追加可能）
            </p>
            <div className="flex flex-wrap gap-1.5">
              {tagsData?.predefined.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs gap-1 pr-1">
                  {tag}
                  <button
                    onClick={() => handleTagDelete(tag)}
                    disabled={tagSaving}
                    className="rounded-full hover:bg-foreground/10 p-0.5 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          {/* カスタムタグ */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">カスタムタグ</Label>
              <span className="text-xs text-muted-foreground">
                {tagsData?.customCount ?? 0}/{isPremium ? 50 : 5}
              </span>
            </div>
            {tagsData?.custom && tagsData.custom.length > 0 ? (
              <div className="space-y-1.5">
                {tagsData.custom.map(tag => (
                  <div key={tag} className="flex items-center gap-2 rounded-lg bg-muted/30 px-3 py-2">
                    {editingTag === tag ? (
                      <>
                        <Input
                          value={tagInput}
                          onChange={e => setTagInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleTagRename(tag);
                            if (e.key === 'Escape') setEditingTag(null);
                          }}
                          className="flex-1 h-8 text-sm"
                          maxLength={30}
                          autoFocus
                          disabled={tagSaving}
                        />
                        <Button size="sm" className="h-8" onClick={() => handleTagRename(tag)} disabled={tagSaving}>
                          保存
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingTag(null)} disabled={tagSaving}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Badge variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          ({tagsData.usage[tag] ?? 0})
                        </span>
                        <div className="ml-auto flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => { setEditingTag(tag); setTagInput(tag); }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-destructive hover:text-destructive"
                            onClick={() => handleTagDelete(tag)}
                            disabled={tagSaving}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                カスタムタグはまだありません
              </p>
            )}

            {/* カスタムタグ追加 */}
            <div className="flex gap-2 mt-3">
              <Input
                placeholder="新しいタグ名..."
                value={newCustomTag}
                onChange={e => setNewCustomTag(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCustomTag()}
                className="flex-1 h-8 text-sm"
                maxLength={30}
                disabled={tagSaving}
              />
              <Button
                size="sm"
                className="h-8 gap-1"
                onClick={handleAddCustomTag}
                disabled={tagSaving || !newCustomTag.trim()}
              >
                <Plus className="h-3.5 w-3.5" />
                追加
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      </motion.div>

      {/* ===================== 3. 表示 (Display) ===================== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.25 }}>
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="rounded-full p-1.5 bg-sky-500/10">
              <Eye className="h-5 w-5 text-sky-500" />
            </div>
            表示
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Card display mode — segment control */}
          <div>
            <Label className="text-sm font-medium mb-2 block">カード表示モード</Label>
            <div className="flex gap-0.5 bg-muted rounded-lg p-0.5">
              {([
                { value: 'compact' as const, label: 'コンパクト' },
                { value: 'standard' as const, label: 'スタンダード' },
                { value: 'rich' as const, label: 'リッチ' },
              ]).map(opt => (
                <button
                  key={opt.value}
                  className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    cardDisplay === opt.value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground'
                  }`}
                  onClick={() => handleCardDisplay(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {cardDisplay === 'compact' && 'タイトルと日付のみ表示'}
              {cardDisplay === 'standard' && 'サマリーとタグを含む表示'}
              {cardDisplay === 'rich' && '本文プレビューを含む詳細表示'}
            </p>
          </div>

          {/* Font size slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">フォントサイズ</Label>
              <span className="text-sm text-muted-foreground">{FONT_SIZE_STEPS[fontSizeIndex].px}</span>
            </div>
            <input
              type="range"
              min={0}
              max={FONT_SIZE_STEPS.length - 1}
              step={1}
              value={fontSizeIndex}
              onChange={e => handleFontSize(Number(e.target.value))}
              className="w-full accent-primary"
            />
            <div className="flex justify-between mt-1">
              {FONT_SIZE_STEPS.map(step => (
                <span key={step.value} className={`text-xs ${fontSizeIndex === step.value ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                  {step.label}
                </span>
              ))}
            </div>
          </div>

          {/* AI Search toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">AI検索</Label>
              <p className="text-xs text-muted-foreground">
                キーワードが正確に一致しなくても、AIが意味を読み取って関連する日記を見つけます
              </p>
            </div>
            <Switch
              checked={aiSearch}
              onCheckedChange={setAiSearch}
            />
          </div>
        </CardContent>
      </Card>
      </motion.div>

      {/* ===================== 4. 通知 (Notifications) ===================== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}>
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="rounded-full p-1.5 bg-amber-500/10">
              <Bell className="h-5 w-5 text-amber-500" />
            </div>
            通知
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* In-app banner */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">アプリ内バナー</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                当日未投稿時にダッシュボードにバナーを表示
              </p>
            </div>
            <Switch
              checked={reminderBanner}
              onCheckedChange={handleReminderBanner}
            />
          </div>

          {/* Browser notifications */}
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <BellRing className="h-4 w-4" />
                  ブラウザ通知
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {notifPermission === 'unsupported'
                    ? 'このブラウザは通知に対応していません'
                    : notifPermission === 'denied'
                      ? 'ブラウザの設定で通知がブロックされています'
                      : '設定した時刻にリマインダー通知を送信'}
                </p>
              </div>
              <Switch
                checked={notifSettings.enabled}
                onCheckedChange={handleNotifToggle}
                disabled={notifPermission === 'unsupported' || notifPermission === 'denied'}
              />
            </div>

            {notifPermission === 'denied' && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 space-y-1.5">
                <p className="text-sm font-medium text-destructive">通知がブロックされています</p>
                <p className="text-xs text-muted-foreground">
                  通知を有効にするには、端末のOS設定またはブラウザの設定から許可してください。
                </p>
                <ol className="text-xs text-muted-foreground list-decimal list-inside space-y-0.5">
                  <li>ブラウザのアドレスバー左の鍵アイコンをタップ</li>
                  <li>「サイトの設定」→「通知」を「許可」に変更</li>
                  <li>このページを再読み込みしてください</li>
                </ol>
              </div>
            )}

            {notifSettings.enabled && (
              <>
                {/* Day selection */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">通知曜日</Label>
                  <div className="flex gap-1">
                    {['日', '月', '火', '水', '木', '金', '土'].map((day, i) => (
                      <button
                        key={i}
                        onClick={() => handleNotifDay(i, !notifSettings.days[i])}
                        className={`flex-1 aspect-square max-w-[44px] rounded-full text-xs font-medium transition-colors ${
                          notifSettings.days[i]
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-accent'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time picker */}
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">通知時刻</Label>
                  <input
                    type="time"
                    value={notifSettings.time}
                    onChange={(e) => handleNotifTime(e.target.value)}
                    className="px-3 py-1.5 rounded-md border bg-background text-sm"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
      </motion.div>

      {/* ===================== 5. プラン (Plan) ===================== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.35 }}>
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="rounded-full p-1.5 bg-yellow-500/10">
              <Crown className="h-5 w-5 text-yellow-500" />
            </div>
            プラン
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">現在のプラン</p>
              <p className="text-xs text-muted-foreground">
                {isPremium ? 'Premium' : 'Free'}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/premium')}
            >
              プラン管理
            </Button>
          </div>
        </CardContent>
      </Card>
      </motion.div>

      {/* ===================== 6. アカウント (Account) ===================== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.4 }}>
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="rounded-full p-1.5 bg-blue-500/10">
              <User className="h-5 w-5 text-blue-500" />
            </div>
            アカウント
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Avatar upload */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative group">
              <Avatar className="h-20 w-20">
                {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
                <AvatarFallback className="text-2xl">{user?.name?.charAt(0)?.toUpperCase() || '?'}</AvatarFallback>
              </Avatar>
              <label className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                {uploadingAvatar
                  ? <Loader2 className="h-5 w-5 text-white animate-spin" />
                  : <Camera className="h-5 w-5 text-white" />
                }
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  disabled={uploadingAvatar}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) {
                      toast({ title: '画像は2MB以下にしてください', variant: 'destructive' });
                      return;
                    }
                    setUploadingAvatar(true);
                    try {
                      await updateUserPicture(file);
                      toast({ title: 'プロフィール画像を更新しました' });
                    } catch {
                      toast({ title: '画像のアップロードに失敗しました', variant: 'destructive' });
                    }
                    setUploadingAvatar(false);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <p className="text-xs text-muted-foreground">タップして変更</p>
          </div>

          <div>
            <Label className="text-sm font-medium mb-1.5 block">ニックネーム</Label>
            {editingName ? (
              <div className="flex gap-2">
                <Input
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') { setEditingName(false); setNameInput(user?.name || ''); } }}
                  className="flex-1"
                  maxLength={30}
                  autoFocus
                />
                <Button size="sm" onClick={handleNameSave}>保存</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditingName(false); setNameInput(user?.name || ''); }}>
                  キャンセル
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm">{user?.name || 'ゲスト'}</p>
                <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => { setNameInput(user?.name || ''); setEditingName(true); }}>
                  <Pencil className="h-3.5 w-3.5" />
                  変更
                </Button>
              </div>
            )}
          </div>
          <div>
            <Label className="text-sm font-medium mb-1 block">メールアドレス</Label>
            <p className="text-sm text-muted-foreground">{user?.email || 'ゲストアカウント'}</p>
          </div>
          <div className="border-t pt-4 space-y-3">
            <Label className="text-sm font-medium">データエクスポート</Label>
            <p className="text-xs text-muted-foreground">
              すべての日記データをダウンロードできます
            </p>
            <div className="flex gap-2">
              {([
                { format: 'json' as const, label: 'JSON' },
                { format: 'csv' as const, label: 'CSV' },
                { format: 'md' as const, label: 'Markdown' },
              ]).map(opt => (
                <Button
                  key={opt.format}
                  variant="outline"
                  size="sm"
                  className="gap-1.5 flex-1"
                  disabled={exporting !== null}
                  onClick={() => handleExport(opt.format)}
                >
                  {exporting === opt.format ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
          <Button
            variant="destructive"
            className="w-full gap-2"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            ログアウト
          </Button>
        </CardContent>
      </Card>
      </motion.div>

      {/* ===================== 7. 法的情報 (Legal) ===================== */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.45 }}>
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="rounded-full p-1.5 bg-gray-500/10">
              <FileText className="h-5 w-5 text-gray-500" />
            </div>
            法的情報
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <a href="/privacy" className="block text-sm text-muted-foreground hover:text-foreground hover:underline">
            プライバシーポリシー
          </a>
          <a href="/terms" className="block text-sm text-muted-foreground hover:text-foreground hover:underline">
            利用規約
          </a>
          <a href="/commercial-transactions" className="block text-sm text-muted-foreground hover:text-foreground hover:underline">
            特定商取引法に基づく表記
          </a>
          <a href="/support" className="block text-sm text-muted-foreground hover:text-foreground hover:underline">
            サポート・お問い合わせ
          </a>
        </CardContent>
      </Card>
      </motion.div>

      {/* ===================== Sticky Save Bar ===================== */}
      {isDirty && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-3"
        >
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">未保存の変更があります</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowDiscardDialog(true)}>
                <Undo2 className="h-3.5 w-3.5" />
                元に戻す
              </Button>
              <Button size="sm" className="gap-1.5" onClick={handleSaveAll}>
                <Save className="h-3.5 w-3.5" />
                保存
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      <PremiumModal
        open={showPremiumModal}
        onOpenChange={setShowPremiumModal}
        onActivated={() => refreshSubscription()}
      />

      {/* AI自動タグOFF確認ダイアログ */}
      <Dialog open={showAutoTagDialog} onOpenChange={setShowAutoTagDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>AI自動タグ付けをOFFにしますか？</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">AIで自動的にタグを付与しません。よろしいですか？</p>
          <DialogFooter className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowAutoTagDialog(false)}>
              キャンセル
            </Button>
            <Button className="flex-1" onClick={() => {
              setAiAutoTag(false);
              setShowAutoTagDialog(false);
            }}>
              OFFにする
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 元に戻す確認ダイアログ */}
      <Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>変更を元に戻しますか？</DialogTitle>
            <DialogDescription>
              設定変更が反映されませんがよろしいですか？
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowDiscardDialog(false)}>
              キャンセル
            </Button>
            <Button variant="destructive" className="flex-1" onClick={() => {
              setShowDiscardDialog(false);
              handleDiscardAll();
            }}>
              元に戻す
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 離脱確認ダイアログ */}
      <Dialog open={showLeaveDialog} onOpenChange={(open) => { if (!open) handleLeaveCancel(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>設定が保存されていません</DialogTitle>
            <DialogDescription>
              変更を保存してから移動しますか？
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={leaveWithoutSaving}>
              保存せず移動
            </Button>
            <Button className="flex-1" onClick={leaveWithSaving}>
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
